import { FiBook } from 'react-icons/fi';

export const awlSublist5Data = {
  courseId: 'awl-sublist-5',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 5',
  icon: FiBook,
  overview: {
    impact: 'Describe analysis and change with precise verbs instead of informal “change,” “make,” and “go down”',
    focus: 'modify, generate, fundamental — plus decline, facilitate, perspective, trend',
    uses: 'Writing Task 1 trends and Task 2 arguments about reform, welfare, and conflict',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word meanings and usage',
      content: `<p>Sublist 5 is the AWL band for change, measurement, and viewpoint. IELTS loves these words because Task 1 needs trend language and Task 2 needs verbs for reform. If your default verbs are “change,” “make,” and “go down,” this list is the upgrade path: <em>alter</em>, <em>amend</em>, <em>generate</em>, <em>decline</em>, <em>expand</em>, <em>evolve</em>.</p>
<p>The full Sublist 5 headword set in this course is: adjust, alter, amend, aware, capacity, challenge, clause, conflict, consult, decline, enable, energy, enforce, equivalent, evolve, expand, expose, external, facilitate, fundamental, generate, logic, monitor, notion, objective, perspective, precise, pursue, ratio, reject, revenue, stable, substitute, sustain, symbol, target, trend, version, welfare, whereas.</p>
<p>The stub focus words <strong>modify</strong> (close in meaning to alter/amend), <strong>generate</strong>, and <strong>fundamental</strong> sit at the centre of high-scoring essays. Generate is not only for electricity: a small change can generate a large improvement. Fundamental names the deepest cause, which is what a Band 7 topic sentence wants.</p>`,
      points: [
        'These words dominate texts about reform, economics, and social change.',
        'Task 1: decline, trend, ratio, stable, expand. Task 2: challenge, conflict, welfare, pursue, fundamental.',
        'Whereas is a contrast linker — treat it as grammar as well as vocabulary.',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['alter', 'To change slightly but with a real effect', 'The school altered the timetable rather than rewriting the whole curriculum.'],
          ['capacity', 'The maximum amount something can contain or produce', 'The stadium has a capacity of 50,000 spectators.'],
          ['challenge', 'A demanding task; to question or test', 'Affordable housing remains a major challenge for growing cities.'],
          ['decline', 'To decrease in quantity, quality, or importance', 'There has been a steady decline in newspaper readership.'],
          ['facilitate', 'To make a process easier', 'Better public transport can facilitate access to jobs.'],
          ['fundamental', 'Forming a necessary base; of central importance', 'Trust is a fundamental requirement of any welfare system.'],
          ['generate', 'To produce or create', 'A small change can generate a large improvement in attendance.'],
          ['objective', 'A goal; not influenced by personal feelings', 'The objective of the reform is to expand capacity, not to cut staff.'],
          ['perspective', 'A particular way of considering something', 'From a parent’s perspective, longer school days look different.'],
          ['pursue', 'To follow or try to achieve over time', 'Many graduates pursue further study to remain competitive.'],
          ['trend', 'A general direction of change', 'The long-term trend is towards remote work, whereas office attendance is less stable.'],
          ['welfare', 'Health, happiness, and prosperity of a person or group', 'Child welfare should outweigh short-term revenue from advertising.'],
        ],
      },
    },
    {
      title: 'Word families',
      content: `<p>Sublist 5 is rich in noun–verb pairs that IELTS uses to paraphrase. <em>Decline</em> is both noun and verb. <em>Challenge</em> is both. <em>Target</em> is both. If the passage says “revenue declined,” a question may say “there was a decline in revenue.” Train your eye for that swap.</p>
<p>Adjective forms carry the evaluation: <em>fundamental</em>, <em>precise</em>, <em>stable</em>, <em>external</em>, <em>equivalent</em>. Adverb forms such as <em>fundamentally</em> and <em>precisely</em> belong in claims: “The two policies are not equivalent; they differ fundamentally.” Watch <em>aware</em> → <em>awareness</em>, a high-frequency Task 2 noun that is easy to misspell.</p>`,
      points: [
        'Many items are noun and verb at once: challenge, decline, target, monitor, conflict.',
        'Generate → generation / generative; choose the slot before you write.',
        'Whereas introduces a contrast clause; it is not a synonym of “and.”',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['alter', 'alteration', 'alter', 'altered, unalterable'],
          ['amend', 'amendment', 'amend', 'amended'],
          ['capacity', 'capacity', '—', '—'],
          ['challenge', 'challenge, challenger', 'challenge', 'challenging'],
          ['decline', 'decline', 'decline', 'declining'],
          ['facilitate', 'facilitation, facilitator', 'facilitate', '—'],
          ['fundamental', 'fundamentals', '—', 'fundamental, fundamentally'],
          ['generate', 'generation', 'generate', 'generative, generated'],
          ['objective', 'objective', '—', 'objective, objectively'],
          ['perspective', 'perspective', '—', '—'],
          ['pursue', 'pursuit', 'pursue', 'pursued'],
          ['sustain', 'sustainability', 'sustain', 'sustainable, sustainably'],
        ],
      },
    },
    {
      title: 'Common collocations',
      content: `<p>Natural partnerships stop Sublist 5 words from sounding dropped-in. “Face a challenge,” “generate revenue,” “a fundamental shift,” and “from a different perspective” are exam-ready chunks. “Make a generate” is not.</p>
<p>For Task 1, lock <em>a downward trend</em>, <em>remain stable</em>, <em>a sharp decline</em>, and <em>the ratio of X to Y</em>. For Task 2, lock <em>enforce a law</em>, <em>consult the public</em>, <em>reject a proposal</em>, and <em>welfare provision</em>. <em>Modify</em> itself is the everyday partner of alter/amend: you modify a plan; you amend a law; you alter a design.</p>`,
      points: [
        'Generate collocates with improvement, energy, revenue, and jobs — not with “happiness” in formal writing.',
        'Fundamental usually modifies change, difference, problem, or principle.',
      ],
      examples: [
        { label: 'challenge', text: 'face a challenge, major challenge, meet the challenge, challenging task' },
        { label: 'decline', text: 'steady decline, sharp decline, decline in, decline to (refuse)' },
        { label: 'generate', text: 'generate improvement, generate revenue, generate energy, generate debate' },
        { label: 'fundamental', text: 'fundamental change, fundamental difference, fundamentally flawed' },
        { label: 'facilitate', text: 'facilitate access, facilitate discussion, facilitate growth' },
        { label: 'perspective', text: 'from the perspective of, a broader perspective, shift in perspective' },
        { label: 'pursue', text: 'pursue a goal, pursue a career, pursue further study' },
        { label: 'trend', text: 'long-term trend, upward trend, reverse the trend' },
        { label: 'welfare', text: 'social welfare, child welfare, welfare system' },
        { label: 'whereas', text: 'X rose, whereas Y remained stable' },
      ],
    },
    {
      title: 'Using Sublist 5 in IELTS writing',
      content: `<p>Task 1: replace “went down a lot” with <em>declined sharply</em> and “stayed the same” with <em>remained stable</em>. Use <em>whereas</em> to contrast two lines on a graph. Task 2: name a <em>fundamental</em> cause, then say what would <em>generate</em> improvement. If you discuss laws, <em>enforce</em> is more precise than “make people follow.”</p>
<p>A compact upgrade: “Cities should not only expand capacity; they should alter the way they allocate space.” That sentence uses two list ideas (expand/capacity, alter) and sounds like a plan, not a complaint. Keep <em>notion</em> for abstract ideas (“the notion that growth is always good”) and <em>objective</em> for measurable goals.</p>`,
      points: [
        'Decline and trend belong in Task 1 overviews.',
        'Generate, facilitate, and pursue belong in solution paragraphs.',
        'Do not confuse decline (decrease / refuse) with recline (lie back).',
      ],
      examples: [
        { label: 'Weak sentence', text: 'A small change can make a big improvement.' },
        { label: 'Strong sentence', text: 'A small change can generate a large improvement.' },
        { label: 'Weak sentence', text: 'The number of readers has gone down a lot.' },
        { label: 'Strong sentence', text: 'There has been a significant decline in newspaper readership.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match word to definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        {
          text: 'What does “generate” mean?',
          options: ['To destroy completely', 'To produce or create', 'To delay a process'],
          answer: 'To produce or create',
          explanation: 'Generate means produce — energy, jobs, revenue, or an improvement. It is not a synonym of delay or destroy.',
        },
        {
          text: 'What does “fundamental” mean?',
          options: ['Slightly useful but optional', 'Forming a necessary base; of central importance', 'Temporary and unofficial'],
          answer: 'Forming a necessary base; of central importance',
          explanation: 'Fundamental describes the deepest layer of a problem or principle, not a minor extra.',
        },
        {
          text: 'What does “facilitate” mean?',
          options: ['To make a process easier', 'To ban a process', 'To measure a process'],
          answer: 'To make a process easier',
          explanation: 'Facilitate is a formal way to say “help a process happen.” It does not mean prohibit or calculate.',
        },
        {
          text: 'What does “decline” mean in a graph description?',
          options: ['To increase rapidly', 'To decrease in quantity or quality', 'To stay exactly the same'],
          answer: 'To decrease in quantity or quality',
          explanation: 'On Task 1, decline is downward movement. Separately, decline can also mean to refuse an offer.',
        },
        {
          text: 'What does “perspective” mean?',
          options: ['A particular way of considering something', 'A type of government tax', 'A machine in a factory'],
          answer: 'A particular way of considering something',
          explanation: 'Perspective is a viewpoint. “From a teacher’s perspective” is a high-value speaking and writing frame.',
        },
      ],
    },
    {
      title: 'Complete the sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct Sublist 5 word.',
      questions: [
        {
          text: 'A small change can ____ a large improvement.',
          answer: 'generate',
          explanation: 'Generate collocates with improvement. After “can” you need the base verb, not generation or generative.',
        },
        {
          text: 'There has been a steady ____ in newspaper readership.',
          answer: 'decline',
          explanation: '“A steady decline in” is the standard Task 1 noun phrase for a downward trend.',
        },
        {
          text: 'Better buses can ____ access to employment in the suburbs.',
          answer: 'facilitate',
          explanation: 'Facilitate means make easier. “Facilitate access” is a natural policy collocation.',
        },
        {
          text: 'Trust is a ____ part of any welfare system.',
          answer: 'fundamental',
          explanation: 'Fundamental is the adjective for a necessary base. It fits before “part,” “change,” or “difference.”',
        },
      ],
    },
    {
      title: 'Choose the correct word form',
      type: 'select',
      instructions: 'Select the grammatically correct form for each sentence.',
      questions: [
        {
          text: 'A small change can ____ a large improvement. (generate)',
          options: ['generate', 'generation', 'generative'],
          answer: 'generate',
          explanation: 'After “can” the verb must be in base form. Generation is a noun; generative is an adjective.',
        },
        {
          text: 'The two approaches are ____ different. (fundamental)',
          options: ['fundamental', 'fundamentally', 'fundamentals'],
          answer: 'fundamentally',
          explanation: 'You need an adverb to modify the adjective “different.” Fundamentals is a plural noun.',
        },
        {
          text: 'Housing remains a ____ issue for young workers. (challenge)',
          options: ['challenge', 'challenging', 'challenged'],
          answer: 'challenging',
          explanation: 'Before the noun “issue” you need an adjective. Challenge as a noun would need a different structure (“a challenge for”).',
        },
        {
          text: 'Exports rose, ____ imports remained stable. (whereas)',
          options: ['whereas', 'where as', 'whereasly'],
          answer: 'whereas',
          explanation: 'Whereas is the contrast linker from this sublist. It is one word and has no adverb form “whereasly.”',
        },
      ],
    },
  ],
  tips: [
    'Treat alter / amend / adjust as a modify-family: plans are altered, laws are amended, settings are adjusted.',
    'Use generate for outcomes (jobs, energy, improvement) and facilitate for processes (access, discussion).',
    'In Task 1, pair decline with a noun phrase: “a decline in X,” not “X was decline.”',
    'Whereas needs two contrasting clauses, not a comma splice onto a fragment.',
  ],
  dailyPractice: [
    'Rewrite a Task 1 overview using decline, trend, stable, and whereas.',
    'Write a 60-word solution paragraph that uses generate and facilitate once each.',
    'Cover the table definitions and recall all twelve target words aloud.',
    'Find a news sentence with challenge, conflict, or welfare and copy the collocation into a notebook.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 5 vocabulary course covering Coxhead headwords such as alter, capacity, challenge, decline, facilitate, fundamental, generate, objective, perspective, pursue, trend, and welfare. Includes the full stub list, word families, collocations, and IELTS Task 1/Task 2 integration for analysis and change.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist5Data,
      title: awlSublist5Data.title + ' (Foundations)',
      overview: { ...awlSublist5Data.overview, duration: '15-20 minutes' },
      sections: awlSublist5Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist5Data.sections.length / 2))),
      exercises: awlSublist5Data.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist5Data,
      title: awlSublist5Data.title + ' (Advanced)',
      overview: { ...awlSublist5Data.overview, duration: '30-35 minutes' },
      sections: awlSublist5Data.sections,
      exercises: awlSublist5Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist5Data.tips || []),
        'For Band 8+: use perspective to show a second stakeholder, then return to your objective.',
        'Advanced tip: contrast revenue and welfare in one evaluated sentence.',
      ],
    };
  }
  return awlSublist5Data;
}
